// The show's board record (157, decision C11), as this console reads and saves it.
//
// ONE RECORD FOR BOARD › SETUP AND THE KIOSK'S LEADERBOARD PIECE. Places, the
// window, how a name is drawn, open places and resetting are saved here once,
// and the piece reads them rather than carrying its own. The service decides
// who may: whoever reads the kiosk playlist reads it (a Viewer for a granted
// show), and an Admin, or an Editor for a client they hold, saves and resets.

import { KIOSK_ENDPOINTS } from "../../../api/src/kiosk/endpoints.js";
import { authenticated, failureFor, type ApiOptions, type Failure } from "./client.js";
import type { SessionToken } from "../session/store.js";

export type BoardWindow = "allTime" | "monthly" | "weekly" | "custom";
export type BoardNames = "tag" | "first";
export type BoardReset = "never" | "daily" | "manual";

export type BoardSettings = {
  readonly places: number;
  readonly window: BoardWindow;
  readonly from: string | null;
  readonly until: string | null;
  readonly names: BoardNames;
  readonly openPlaces: boolean;
  readonly reset: BoardReset;
};

export type BoardDesk = {
  readonly activationId: string;
  /** False is the defaults: nobody has set this show's board up. */
  readonly saved: boolean;
  readonly settings: BoardSettings;
  readonly revisedAt: string | null;
  readonly clearedAt: string | null;
  /** The moment the board counts from, or null for every run in its window. */
  readonly since: string | null;
};

export type BoardRead = { readonly ok: true; readonly value: BoardDesk } | ({ readonly ok: false } & Failure);

export const BOARD_WINDOW_LABEL: Readonly<Record<BoardWindow, string>> = {
  allTime: "All time",
  monthly: "This month",
  weekly: "Last 7 days",
  custom: "Custom dates",
};

export const BOARD_NAMES_LABEL: Readonly<Record<BoardNames, string>> = {
  tag: "Player tag",
  first: "First name and initial",
};

export const BOARD_RESET_LABEL: Readonly<Record<BoardReset, string>> = {
  never: "Never",
  daily: "Each day at close",
  manual: "Only when I reset it",
};

export const BOARD_MAX_PLACES = 20;

export async function readBoard(
  options: ApiOptions,
  token: SessionToken,
  input: { readonly activationId: string; readonly clientId: string },
): Promise<BoardRead> {
  const answer = await authenticated(options, token, {
    path:
      `${KIOSK_ENDPOINTS.board}?activation_id=${encodeURIComponent(input.activationId)}` +
      `&client_id=${encodeURIComponent(input.clientId)}`,
    method: "GET",
  });
  if (!answer.ok) return { ok: false, ...withIssues(answer) };
  return deskOrUnreadable(answer.body, input.activationId, "read");
}

export async function saveBoard(
  options: ApiOptions,
  token: SessionToken,
  input: { readonly activationId: string; readonly clientId: string; readonly settings: BoardSettings },
): Promise<BoardRead> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.board,
    method: "PUT",
    json: {
      activation_id: input.activationId,
      client_id: input.clientId,
      board: {
        places: input.settings.places,
        window: input.settings.window,
        from: input.settings.window === "custom" ? input.settings.from : null,
        until: input.settings.window === "custom" ? input.settings.until : null,
        names: input.settings.names,
        open_places: input.settings.openPlaces,
        reset: input.settings.reset,
      },
    },
  });
  if (!answer.ok) return { ok: false, ...withIssues(answer) };
  return deskOrUnreadable(answer.body, input.activationId, "saved");
}

/** Reset now. The board starts again from nobody; the game's records keep every score. */
export async function resetBoard(
  options: ApiOptions,
  token: SessionToken,
  input: { readonly activationId: string; readonly clientId: string },
): Promise<BoardRead> {
  const answer = await authenticated(options, token, {
    path: KIOSK_ENDPOINTS.boardClear,
    method: "POST",
    json: { activation_id: input.activationId, client_id: input.clientId },
  });
  if (!answer.ok) return { ok: false, ...withIssues(answer) };
  return deskOrUnreadable(answer.body, input.activationId, "reset");
}

/** The service's own sentence, with the settings it named, so a refused save says which. */
function withIssues(answer: Parameters<typeof failureFor>[0]): Failure {
  const failure = failureFor(answer);
  const body = (answer.body ?? {}) as Record<string, unknown>;
  const issues = Array.isArray(body["issues"]) ? (body["issues"] as unknown[]) : [];
  const said = issues
    .map((issue) => (issue !== null && typeof issue === "object" ? (issue as Record<string, unknown>)["message"] : null))
    .filter((message): message is string => typeof message === "string" && message !== "");
  if (said.length === 0) return failure;
  return { ...failure, detail: `${failure.detail}: ${said.join("; ")}.` } as Failure;
}

function deskOrUnreadable(body: unknown, activationId: string, did: "read" | "saved" | "reset"): BoardRead {
  const desk = boardDeskFrom(body, activationId);
  if (desk !== null) return { ok: true, value: desk };
  return {
    ok: false,
    kind: "unavailable",
    detail:
      did === "read"
        ? "The service answered a board this console could not read. Nothing is drawn from it."
        : `The service ${did} the board and then answered in a shape this console could not read. Open the page again.`,
  };
}

export function boardDeskFrom(body: unknown, activationId: string): BoardDesk | null {
  const row = fields(body);
  const board = fields(row["board"]);
  const places = board["places"];
  if (typeof places !== "number" || !Number.isInteger(places) || places < 1 || places > BOARD_MAX_PLACES) return null;
  const window = board["window"];
  if (window !== "allTime" && window !== "monthly" && window !== "weekly" && window !== "custom") return null;
  const names = board["names"];
  if (names !== "tag" && names !== "first") return null;
  const reset = board["reset"];
  if (reset !== "never" && reset !== "daily" && reset !== "manual") return null;
  return {
    activationId: text(row["activation_id"]) ?? activationId,
    saved: row["saved"] === true,
    settings: {
      places,
      window,
      from: text(board["from"]),
      until: text(board["until"]),
      names,
      openPlaces: board["open_places"] !== false,
      reset,
    },
    revisedAt: text(row["revised_at"]),
    clearedAt: text(row["cleared_at"]),
    since: text(row["since"]),
  };
}

/** "Last 7 days · 10 places", or "Custom dates, 1 Oct to 14 Oct · 10 places": the record in one line. */
export function boardSummary(settings: BoardSettings): string {
  const window =
    settings.window === "custom"
      ? `Custom dates, ${settings.from ?? "the first run"} to ${settings.until ?? "today"}`
      : BOARD_WINDOW_LABEL[settings.window];
  return `${window} · ${settings.places} place${settings.places === 1 ? "" : "s"}`;
}

function fields(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}
